<template>
  <div class="switcher">
    <div class="top">
      <button
        class="p-button p-button-sm p-button-text p-button-outlined mr-2"
        @click="showBigCharts = !showBigCharts"
      >
        <icon :icon="['far', showBigCharts ? 'compress' : 'expand-arrows']" />
      </button>

      <span class="mr-2">Data type</span>
      <Dropdown
        v-model="selectedOption"
        :options="chartOptions"
        append-to="body"
      />
    </div>

    <div v-if="selectedOption === 'Pump Table'" class="pumpContainer">
      <PumpCalcTable class="middle" />
    </div>

    <div v-if="selectedOption === 'Single'">
      <SinglePumpChart
        draw-legend
        enable-mouse-tracking
        hide-pump-selector
        :minimum-height-px="chartMinHeight"
        :aspect-ratio="10"
        :npsh-mode="!isEspOnShore"
        :display-npsh-toggle="!isEspOnShore"
      />
    </div>

    <PumpTornadoChartSet
      v-if="selectedOption === 'Tornado'"
      hide-pump-selector
      :chart-min-height="chartMinHeight"
    />

    <TaperedPumpsChartSet
      v-if="selectedOption === 'Tapered'"
      :minimum-height-px="chartMinHeight"
    />

    <div v-if="selectedOption === 'Gas'">
      <GasCurveChart
        draw-legend
        enable-mouse-tracking
        :aspect-ratio="5"
        :minimum-height-px="chartMinHeight"
      />
    </div>

    <div v-if="selectedOption === 'Deviation'" class="two-up">
      <DeviationSurveyDepth
        title="Survey Depth"
        enable-mouse-tracking
        mouse-tracking-ignore-y
        :minimum-height-px="chartMinHeight"
        :aspect-ratio="5"
      />
      <DeviationSurveyTop
        v-if="is3dSurvey"
        title="Azimuth Visualization"
        :show-no-data="!is3dSurvey"
        enable-mouse-tracking
        :minimum-height-px="chartMinHeight"
        :aspect-ratio="5"
      />
    </div>
    <div v-if="selectedOption === 'Pressure Traverse'">
      <PressureTraverseChart
        enable-mouse-tracking
        :minimum-height-px="chartMinHeight"
        :aspect-ratio="10"
      />
    </div>
    <div v-if="selectedOption === 'IPR @ Datum'">
      <IPRChart
        enable-mouse-tracking
        draw-legend
        :minimum-height-px="chartMinHeight"
        :aspect-ratio="10"
      />
    </div>
    <div v-if="selectedOption === 'Speed Torque'">
      <PumpSpeedTorque
        enable-mouse-tracking
        :minimum-height-px="chartMinHeight"
        :aspect-ratio="10"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";

import DeviationSurveyDepth from "@/components/scenarios/charts/DeviationSurveyDepth.vue";
import DeviationSurveyTop from "@/components/scenarios/charts/DeviationSurveyTop.vue";
import {
  scenarioCalculationsKey,
  scenarioKey,
  wellPropertiesKey
} from "@/composables/scenarioDetailProvider/scenarioDetailProviderKeys";
import { useSettingsStore } from "@/store";
import { usePrefetchTornadoPredictions } from "@/libs/charting/tornado/worker/usePrefetchTornadoPredictions";
import { injectStrict } from "@/utils/utils";
import SinglePumpChart from "@/components/scenarios/charts/SinglePumpChart.vue";
import PumpCalcTable from "@/components/scenarios/PumpCalcTable.vue";
import PumpTornadoChartSet from "./PumpTornadoChartSet.vue";
import GasCurveChart from "@/components/scenarios/charts/GasCurveChart.vue";
import PressureTraverseChart from "./PressureTraverseChart.vue";
import IPRChart from "./IPRChart.vue";
import TaperedPumpsChartSet from "./TaperedPumpsChartSet.vue";
import PumpSpeedTorque from "./PumpSpeedTorque.vue";
import {
  Deviation_Survey_Directional_Dataset_Enum,
  Sizing_Pumping_Type_Enum
} from "gql/api";

const props = defineProps<{
  espOnShore?: string | null | undefined;
}>();

const scenario = injectStrict(scenarioKey);
const wellProperties = injectStrict(wellPropertiesKey);

// Get the Tornado chart's predictions going in the background, so it is
// ready sooner when selected. Uses the same calcs the chart does.
const { flags } = useSettingsStore();
usePrefetchTornadoPredictions({
  scenarioCalculations: injectStrict(scenarioCalculationsKey),
  minFrequency: () => flags.value.minimumPumpChartFrequency,
  maxFrequency: () => flags.value.maximumPumpChartFrequency
});

const showBigCharts = ref(false);
const chartMinHeight = computed(() => (showBigCharts.value ? 700 : 400));

const isEspOnShore = computed(
  () => props.espOnShore === Sizing_Pumping_Type_Enum.EspOnShore
);

const baseChartOptions = [
  "Pump Table",
  "Single",
  "Tornado",
  "Tapered",
  "Gas",
  "Deviation",
  "Pressure Traverse",
  "IPR @ Datum"
];
// need to recreate chartOptions and filter out "IPR @ Datum" and "Deviation" when isEspOnShore is true
const chartOptions = computed(() =>
  !isEspOnShore.value
    ? [
        ...baseChartOptions.filter(
          o =>
            o !== "Deviation" &&
            o !== "IPR @ Datum" &&
            o !== "Pressure Traverse"
        ),
        "Speed Torque"
      ]
    : baseChartOptions
);

const selectedOption = ref("Pump Table");

const deviationSurvey = computed(
  () =>
    scenario.value?.well_properties?.r_deviation_survey ??
    wellProperties.value?.r_deviation_survey
);

const is3dSurvey = computed(
  () =>
    deviationSurvey.value?.directional_data_set ===
      Deviation_Survey_Directional_Dataset_Enum.ThreeDMdInclinationAzimuth &&
    scenario.value?.well_properties?.deviation_survey_id !== null
);
</script>

<style lang="scss" scoped>
.switcher {
  & > :deep(.ddv) {
    margin: 0 auto;
    max-width: 2000px;
  }
  .top {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    padding: 0.5em;
  }
  .two-up {
    display: grid;
    gap: 1em;
    grid-template-columns: repeat(auto-fit, minmax(360px, 1fr));
  }
}

.middle {
  padding: 0.5em;
  width: 100%;
}

.pumpContainer {
  width: 100%;
  display: flex;
  justify-content: center;
}
</style>
